import { CASE_EXPOSURES } from '@app/constants/feature-definitions';
import { isAllowedCaseTypesScopingEnabled } from '@app/helpers/featureFlags';
import type { ExposureType } from '@app/services/exposures';
import type { EffectiveFeatureMap } from '@app/services/organization-features';
import { type Job } from '@app/types/api/job';

export type ExposureWall =
  | { status: 'pending' }
  | { status: 'off' }
  | { status: 'on'; heldRestrictedTypeIds: string[] };

export interface CaseVisibilityScope {
  allowedCaseTypes: string[];
  exposureWall: ExposureWall;
}

// Default to apply when a case should be visible to all users.
export const UNSCOPED_CASE_VISIBILITY: CaseVisibilityScope = {
  allowedCaseTypes: [],
  exposureWall: { status: 'off' },
};

export const resolveExposureWall = (
  featureMap: EffectiveFeatureMap | undefined,
  exposureTypes: ExposureType[] | undefined,
  exposureSkills?: string[] | null,
): ExposureWall => {
  if (!featureMap) return { status: 'pending' };
  if (!featureMap[CASE_EXPOSURES]) return { status: 'off' };
  if (!exposureTypes) return { status: 'pending' };
  const restrictedTypeIds = exposureTypes
    .filter((type) => type.restricted)
    .map((type) => type.id);
  if (!restrictedTypeIds.length) return { status: 'off' };
  return {
    status: 'on',
    heldRestrictedTypeIds: restrictedTypeIds.filter((id) =>
      exposureSkills?.includes(id),
    ),
  };
};

type VisibilityInputs = Partial<
  Pick<Job, 'type' | 'exposureTypeIds' | 'hasRestrictedExposureTypes'>
>;

export const isCaseVisibleUnderExposureWall = (
  job: VisibilityInputs | null | undefined,
  exposureWall: ExposureWall,
): boolean => {
  if (!job?.hasRestrictedExposureTypes) return true;
  if (exposureWall.status === 'pending') return false;
  if (exposureWall.status === 'off') return true;
  return (job.exposureTypeIds ?? []).some((id) =>
    exposureWall.heldRestrictedTypeIds.includes(id),
  );
};

export const isCaseVisibleToViewer = (
  job: VisibilityInputs | null | undefined,
  { allowedCaseTypes, exposureWall }: CaseVisibilityScope,
): boolean => {
  if (
    isAllowedCaseTypesScopingEnabled() &&
    allowedCaseTypes.length > 0 &&
    !allowedCaseTypes.includes(job?.type ?? '')
  ) {
    return false;
  }
  return isCaseVisibleUnderExposureWall(job, exposureWall);
};
